/**
 * Ableitungen aus dem Markdown-Text. Titel, Vorschau, Tags und Properties werden
 * immer neu berechnet und nie separat gespeichert — damit kann der Text nie von
 * seinen Metadaten abweichen.
 */

const FRONTMATTER = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

export type Frontmatter = {
  data: Record<string, string>;
  body: string;
  raw: string;
};

export function parseFrontmatter(content: string): Frontmatter {
  const match = FRONTMATTER.exec(content);
  if (!match) return { data: {}, body: content, raw: '' };
  const data: Record<string, string> = {};
  for (const line of (match[1] ?? '').split(/\r?\n/)) {
    const separator = line.indexOf(':');
    if (separator < 1) continue;
    const key = line.slice(0, separator).trim();
    const value = line.slice(separator + 1).trim();
    if (key) data[key] = value;
  }
  return { data, body: content.slice(match[0].length), raw: match[0] };
}

function stripMarks(line: string): string {
  return line
    .replace(/^#{1,6}\s+/, '')
    .replace(/^>\s?/, '')
    .replace(/^[-*+]\s+(\[[ xX]\]\s+)?/, '')
    .replace(/^\d+\.\s+/, '')
    .replace(/\[\[([^\]|]+)(\|[^\]]+)?\]\]/g, '$1')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_~`]/g, '')
    .trim();
}

export function deriveTitle(content: string): string {
  const { body } = parseFrontmatter(content);
  for (const line of body.split(/\r?\n/)) {
    const clean = stripMarks(line);
    if (clean) return clean.slice(0, 120);
  }
  return 'Ohne Titel';
}

/** Zählt die Zeilen in secret-Blöcken — die Vorschau sagt, wie viel verdeckt ist. */
export function countSecretFields(content: string): number {
  let fields = 0;
  for (const match of content.matchAll(/^```secret[^\n]*\n([\s\S]*?)^```/gm)) {
    fields += (match[1] ?? '').split(/\r?\n/).filter((line) => line.trim() !== '').length;
  }
  return fields;
}

/** Vorschautext für die Liste — ohne Titelzeile und ohne Inhalte von secret-Blöcken. */
export function derivePreview(content: string): string {
  const { body } = parseFrontmatter(content);
  const lines = forDisplay(body).split(/\r?\n/);
  const out: string[] = [];
  let titleSeen = false;
  for (const line of lines) {
    const clean = stripMarks(line);
    if (!clean) continue;
    if (!titleSeen) {
      titleSeen = true;
      continue;
    }
    out.push(clean);
    if (out.join(' ').length > 160) break;
  }
  const text = out.join(' ').slice(0, 200);
  const secrets = countSecretFields(content);
  if (secrets === 0) return text;
  const marker = `secret · ${secrets} ${secrets === 1 ? 'Feld' : 'Felder'} maskiert`;
  return text ? `${marker} · ${text}` : marker;
}

/**
 * Aufbereitung für Vorschau und Suchindex: Kennwörter verschwinden, und die Abfrage
 * eines Datenbank-Blocks wird zu einem Wort — sie ist Konfiguration, kein Inhalt, und
 * hat in der Vorschau nichts verloren.
 */
export function forDisplay(content: string): string {
  return content
    .replace(/^```secret[^\n]*\n[\s\S]*?^```/gm, '')
    .replace(/^```athene-db\r?\n[\s\S]*?^```/gm, 'Tabelle');
}

/** Ersetzt den Inhalt von ```secret-Blöcken, bevor Text irgendwo angezeigt wird. */
export function redactSecrets(content: string): string {
  return content.replace(/^```secret[^\n]*\n([\s\S]*?)^```/gm, '```secret\n••••••••\n```');
}

export function extractTags(content: string): string[] {
  const { data, body } = parseFrontmatter(content);
  const tags = new Set<string>();
  const raw = data['tags'];
  if (raw) {
    for (const tag of raw.replace(/^\[|\]$/g, '').split(',')) {
      const clean = tag.trim().replace(/^["']|["']$/g, '');
      if (clean) tags.add(clean.toLowerCase());
    }
  }
  // Inline-Tags, aber nicht in Codeblöcken und nicht als Überschrift.
  const withoutCode = body.replace(/```[\s\S]*?```/g, '').replace(/`[^`]*`/g, '');
  for (const match of withoutCode.matchAll(/(^|\s)#([\p{L}\p{N}_/-]{2,})/gu)) {
    tags.add((match[2] ?? '').toLowerCase());
  }
  return [...tags];
}

export function countCharacters(content: string): number {
  return content.length;
}
